// Eslint disable required since we cannot add keydown handler to the trigger component
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { UncontrolledPopper } from '@newfront-insurance/core-ui';
import { cn } from '@newfront-insurance/core-ui/v2';
import * as React from 'react';

export type QuickSelectOptions = { [key: string]: number };

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void;
  triggerComponent: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  quickSelectOptions: QuickSelectOptions;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getValueFromIndex(index: number, options: QuickSelectOptions): any {
  return options[Object.keys(options)[index]];
}

export function InputQuickSelect(props: Props) {
  const { onChange, triggerComponent, quickSelectOptions } = props;
  // Check if value is in the quick select options and get the index
  const [showingSuggestions, setShowingSuggestions] = React.useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = React.useState(0);
  const baseSuggestionClassName = 'py-2 pl-6 pr-12 text-base';

  // Handles toggle and showing suggestions
  const handleShowSuggestions = (toggle: (isOpen?: boolean) => void) => (isOpen: boolean) => {
    setShowingSuggestions(isOpen);
    toggle(isOpen);
  };

  // Handle keyboard events when the trigger component is focused
  const handleKeyDown = (event: React.KeyboardEvent, toggle: (isOpen?: boolean) => void) => {
    if (event.key === 'ArrowDown') {
      if (showingSuggestions) {
        // If suggestions are already shown, cycle through them
        setActiveSuggestionIndex((prev) => (prev + 1) % Object.keys(quickSelectOptions).length);
      } else {
        // If suggestions are not shown, show them
        handleShowSuggestions(toggle)(true);
      }
    } else if (event.key === 'ArrowUp') {
      setActiveSuggestionIndex(
        (prev) => (prev - 1 + Object.keys(quickSelectOptions).length) % Object.keys(quickSelectOptions).length,
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      onChange(getValueFromIndex(activeSuggestionIndex, quickSelectOptions));
      handleShowSuggestions(toggle)(false);
    } else if (event.key === ' ') {
      if (showingSuggestions) {
        // If suggestions are shown, select the active suggestion
        onChange(getValueFromIndex(activeSuggestionIndex, quickSelectOptions));
        handleShowSuggestions(toggle)(false);
      } else {
        // If suggestions are not shown, show them
        handleShowSuggestions(toggle)(true);
      }
    }
  };

  return (
    <UncontrolledPopper
      // eslint-disable-next-line react/no-unstable-nested-components
      trigger={({ toggle, triggerRef }) => (
        <div
          onFocus={() => handleShowSuggestions(toggle)(true)}
          onBlur={() => handleShowSuggestions(toggle)(false)}
          onKeyDown={(event) => handleKeyDown(event, toggle)}
          ref={triggerRef}
        >
          {triggerComponent}
        </div>
      )}
      style={{ borderRadius: 0, border: 0 }}
    >
      {({ toggle }) => (
        <div className="border-steel-200 flex flex-col rounded-md border bg-white py-2">
          <div className={`${baseSuggestionClassName} text-steel-400`}>Quick select</div>
          {Object.keys(quickSelectOptions).map((key, index) => {
            const isActive = index === activeSuggestionIndex;
            const className = cn(`${baseSuggestionClassName} inline text-start`, {
              'bg-brand-400 text-white': isActive,
              'text-steel-500': !isActive,
            });
            const setAsActive = () => setActiveSuggestionIndex(index);
            return (
              <button
                className={className}
                key={key}
                onMouseOver={setAsActive}
                onFocus={setAsActive}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  onChange(quickSelectOptions[key]);
                  handleShowSuggestions(toggle)(false);
                }}
                type="button"
                // Remove suggestions from tab order
                tabIndex={-1}
              >
                {key}
              </button>
            );
          })}
        </div>
      )}
    </UncontrolledPopper>
  );
}
